import { useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { Elements } from '@stripe/react-stripe-js';
import { Header } from '@/components/layout/Header';
import { Footer } from '@/components/layout/Footer';
import { Button } from '@/components/ui/button';
import { PaymentMethodList } from '@/components/billing/PaymentMethodList';
import { PaymentMethodForm } from '@/components/billing/PaymentMethodForm';
import { DowngradeBanner } from '@/components/billing/DowngradeBanner';
import { CancellationBanner } from '@/components/billing/CancellationBanner';
import {
  useSubscription,
  usePaymentMethods,
  isSubscriptionActive,
  isScheduledForCancellation,
  hasScheduledDowngrade,
  formatPrice,
} from '@/hooks/useSubscription';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from '@/components/ui/use-toast';
import { getStripe } from '@/lib/stripe';
import {
  CreditCard,
  ArrowRight,
  Loader2,
  Plus,
  AlertTriangle,
} from 'lucide-react';

export function BillingPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const {
    subscription,
    isLoading: isSubLoading,
    createSetupIntent,
    scheduleCancellation,
    isScheduleCancellationLoading,
  } = useSubscription();
  const {
    paymentMethods,
    isLoading: isPmLoading,
    removePaymentMethod,
    isRemoveLoading,
    setDefaultPaymentMethod,
    isSetDefaultLoading,
    savePaymentMethod,
    isSaveLoading,
  } = usePaymentMethods();

  const [showAddCard, setShowAddCard] = useState(false);
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [removingId, setRemovingId] = useState<string | undefined>();
  const [settingDefaultId, setSettingDefaultId] = useState<string | undefined>();

  const isActive = subscription?.status && isSubscriptionActive(subscription.status);
  const isCancelScheduled = isScheduledForCancellation(subscription ?? null);
  const isDowngradeScheduled = hasScheduledDowngrade(subscription ?? null);

  const handleChangePlan = () => {
    navigate({ to: '/app/plan-selection' });
  };

  const handleAddCard = async () => {
    try {
      const result = await createSetupIntent();
      if (result?.clientSecret) {
        setClientSecret(result.clientSecret);
        setShowAddCard(true);
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to initialize payment setup',
        variant: 'destructive',
      });
    }
  };

  const handlePaymentMethodSuccess = async (paymentMethodId: string) => {
    try {
      await savePaymentMethod({ paymentMethodId, setAsDefault: !paymentMethods?.length });
      setShowAddCard(false);
      setClientSecret(null);
      toast({
        title: 'Card saved',
        description: 'Your payment method has been added.',
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to save payment method',
        variant: 'destructive',
      });
    }
  };

  const handleRemoveCard = async (paymentMethodId: string) => {
    setRemovingId(paymentMethodId);
    try {
      await removePaymentMethod({ paymentMethodId });
      toast({
        title: 'Card removed',
        description: 'Your payment method has been removed.',
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to remove payment method',
        variant: 'destructive',
      });
    } finally {
      setRemovingId(undefined);
    }
  };

  const handleSetDefault = async (paymentMethodId: string) => {
    setSettingDefaultId(paymentMethodId);
    try {
      await setDefaultPaymentMethod({ paymentMethodId });
      toast({
        title: 'Default updated',
        description: 'Your default payment method has been changed.',
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to update default',
        variant: 'destructive',
      });
    } finally {
      setSettingDefaultId(undefined);
    }
  };

  const handleCancelSubscription = async () => {
    try {
      await scheduleCancellation();
      toast({
        title: 'Cancellation scheduled',
        description: 'Your subscription will end at the current billing period.',
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to schedule cancellation',
        variant: 'destructive',
      });
    }
  };

  // Extract feature list with proper typing
  const featureList: string[] = Array.isArray(subscription?.current_plan?.features?.feature_list)
    ? (subscription.current_plan!.features.feature_list as string[])
    : [];

  // Loading state
  if (isSubLoading) {
    return (
      <div className="flex min-h-screen flex-col">
        <Header />
        <main className="flex flex-1 items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col">
      <Header />

      <main className="flex-1 py-8">
        <div className="container">
          <div className="mx-auto max-w-2xl">
            <h1 className="font-display text-2xl font-bold sm:text-3xl">
              Billing & Subscription
            </h1>
            <p className="mt-1 text-muted-foreground">
              Manage your plan, payment methods, and billing preferences.
            </p>

            {/* Banners */}
            {isCancelScheduled && (
              <div className="mt-6">
                <CancellationBanner periodEndDate={subscription?.current_period_end ?? null} />
              </div>
            )}

            {isDowngradeScheduled && subscription?.scheduled_plan && subscription?.scheduled_plan_change_date && (
              <div className="mt-6">
                <DowngradeBanner
                  scheduledPlan={subscription.scheduled_plan}
                  scheduledDate={subscription.scheduled_plan_change_date}
                />
              </div>
            )}

            {/* Current Plan Section */}
            <section className="mt-8">
              <h2 className="font-display text-lg font-semibold">Current Plan</h2>

              {subscription && isActive ? (
                <div className="mt-3 rounded-card border bg-card p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-display text-xl font-semibold capitalize">
                        {subscription.current_plan?.plan_name || subscription.plan_type}
                      </p>
                      {subscription.current_plan && (
                        <p className="mt-1 text-muted-foreground">
                          {formatPrice(subscription.current_plan.price)}
                          /{subscription.current_plan.billing_period === 'yearly' ? 'year' : 'month'}
                        </p>
                      )}
                    </div>
                    <div className="text-right">
                      <span className={`inline-block rounded-full px-3 py-1 text-xs font-medium ${
                        isCancelScheduled
                          ? 'bg-warning-100/10 text-warning-200'
                          : 'bg-sapling-primary-100/10 text-sapling-primary-100'
                      }`}>
                        {isCancelScheduled
                          ? 'Ending'
                          : subscription.status === 'internal'
                            ? 'Admin'
                            : subscription.status}
                      </span>
                      {subscription.current_period_end && (
                        <p className="mt-1 text-sm text-muted-foreground">
                          {isCancelScheduled ? 'Ends' : 'Renews'}{' '}
                          {new Date(subscription.current_period_end).toLocaleDateString()}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Features list */}
                  {featureList.length > 0 && (
                    <div className="mt-4 border-t pt-4">
                      <p className="text-sm font-medium text-muted-foreground">Plan features:</p>
                      <ul className="mt-2 grid gap-1 text-sm text-muted-foreground sm:grid-cols-2">
                        {featureList.map((feature) => (
                          <li key={feature} className="flex items-center gap-2">
                            <span className="text-sapling-primary-100">&#10003;</span>
                            {feature}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  <div className="mt-4 flex gap-3 border-t pt-4">
                    <Button variant="outline" size="sm" onClick={handleChangePlan}>
                      Change Plan
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                    {!isCancelScheduled && subscription.status !== 'internal' && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={handleCancelSubscription}
                        loading={isScheduleCancellationLoading}
                        className="text-muted-foreground hover:text-destructive"
                      >
                        Cancel Subscription
                      </Button>
                    )}
                  </div>
                </div>
              ) : (
                <div className="mt-3 rounded-card border border-dashed bg-muted/50 p-6 text-center">
                  <p className="text-muted-foreground">No active subscription</p>
                  <Button className="mt-4" onClick={handleChangePlan}>
                    Choose a Plan
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </div>
              )}
            </section>

            {/* Payment Methods Section */}
            <section className="mt-8">
              <div className="flex items-center justify-between">
                <h2 className="font-display text-lg font-semibold">Payment Methods</h2>
                {!showAddCard && (
                  <Button variant="outline" size="sm" onClick={handleAddCard}>
                    <Plus className="mr-2 h-4 w-4" />
                    Add Card
                  </Button>
                )}
              </div>

              <div className="mt-3">
                {showAddCard && clientSecret ? (
                  <div className="rounded-card border bg-card p-6">
                    <h3 className="mb-4 font-medium">Add Payment Method</h3>
                    <Elements
                      stripe={getStripe()}
                      options={{
                        clientSecret,
                        appearance: {
                          theme: 'stripe',
                          variables: {
                            colorPrimary: '#22C55E',
                            borderRadius: '8px',
                          },
                        },
                      }}
                    >
                      <PaymentMethodForm
                        clientSecret={clientSecret}
                        onSuccess={handlePaymentMethodSuccess}
                        onCancel={() => {
                          setShowAddCard(false);
                          setClientSecret(null);
                        }}
                        submitLabel="Save Card"
                        loading={isSaveLoading}
                      />
                    </Elements>
                  </div>
                ) : isPmLoading ? (
                  <div className="flex items-center justify-center py-6">
                    <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                  </div>
                ) : (
                  <PaymentMethodList
                    paymentMethods={paymentMethods ?? []}
                    onRemove={handleRemoveCard}
                    onSetDefault={handleSetDefault}
                    isRemoveLoading={isRemoveLoading}
                    isSetDefaultLoading={isSetDefaultLoading}
                    removingId={removingId}
                    settingDefaultId={settingDefaultId}
                  />
                )}
              </div>
            </section>

            {/* Past Due Warning */}
            {subscription?.status === 'past_due' && (
              <section className="mt-8">
                <div className="flex items-center gap-3 rounded-card border border-warning-200 bg-warning-100/10 p-4">
                  <AlertTriangle className="h-5 w-5 shrink-0 text-warning-200" />
                  <div>
                    <p className="font-medium text-warning-200">Payment past due</p>
                    <p className="text-sm text-muted-foreground">
                      Your last payment failed. Please update your payment method to avoid service interruption.
                    </p>
                  </div>
                </div>
              </section>
            )}

            {/* Back to Dashboard */}
            <div className="mt-8">
              <Button variant="ghost" onClick={() => navigate({ to: '/app/dashboard' })}>
                Back to Dashboard
              </Button>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
